import {
  Component,
  Host,
  h,
  Element,
  Event,
  EventEmitter,
  Prop,
  Watch,
  State,
} from '@stencil/core';

export interface AhaSidepanelToggleOpenEvent {
  target: HTMLAhaSidepanelElement;
  open: boolean;
}

export interface AhaSidepanel extends HTMLAhaSidepanelElement {}

/**
 * A sidepanel, similar to the drawer, but it expands the sidebar instead of being
 * on top of the content (drawer is rendered on top). Used initially in whiteboards
 * and notes fullscreen for sidepanel (e.g. comments).
 *
 * These components are style-only components for now, they do not provide any
 * functionality out of the box.
 *
 * @slot default - Contents of the default slot.
 */
@Component({
  tag: 'aha-sidepanel',
  styleUrl: 'aha-sidepanel.css',
  shadow: true,
})
export class AhaSidepanel {
  private static readonly DEFAULT_AND_MIN_WIDTH = 380;
  private static readonly MAX_WIDTH = 800;

  /** Whether or not the sidepanel is currently open/visible. */
  @Prop({ reflect: true, mutable: true }) open: boolean = false;

  @State() isResizing: boolean = false;
  @State() currentWidth: number = AhaSidepanel.DEFAULT_AND_MIN_WIDTH;

  private configuredMinWidth: number = AhaSidepanel.DEFAULT_AND_MIN_WIDTH;

  private isDragging = false;
  private startX = 0;
  private startWidth = 0;

  /** Event for toggling the sidepanel */
  @Event({
    eventName: 'aha-sidepanel:toggleopen',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) // eslint-disable-line @stencil/decorators-style
  toggleOpen!: EventEmitter<AhaSidepanelToggleOpenEvent>;

  private handleClick = (event: Event) => {
    this.open = !this.open;
  };

  @Watch('open')
  private openChanged() {
    this.toggleOpen.emit({ target: this.host, open: this.open });
  }

  @Element() host!: HTMLAhaSidepanelElement;

  componentDidLoad() {
    this.setupResizing();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  private cleanup() {
    if (this.isDragging) {
      document.removeEventListener('mousemove', this.handleResizeMouseMove);
      document.removeEventListener('mouseup', this.handleResizeMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.host.removeAttribute('data-resizing');
      this.isDragging = false;
      this.isResizing = false;
    }
  }

  private setWidth(width: number) {
    this.currentWidth = width;
    this.host.style.setProperty('--aha-sidepanel--menu-width', `${width}px`);
    this.host.style.setProperty('--side-panel--menu-width', `${width}px`);
  }

  private setupResizing() {
    // Store the configured minimum width from CSS custom property if provided
    const cssMinWidth = getComputedStyle(this.host).getPropertyValue('--aha-sidepanel--menu-width');

    this.configuredMinWidth = cssMinWidth
      ? parseInt(cssMinWidth, 10)
      : AhaSidepanel.DEFAULT_AND_MIN_WIDTH;
    this.setWidth(this.configuredMinWidth);
  }

  private handleResizeMouseDown = (event: MouseEvent) => {
    if (!this.open) return;

    event.preventDefault();
    this.isDragging = true;
    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.currentWidth || this.configuredMinWidth;

    this.host.setAttribute('data-resizing', '');

    document.addEventListener('mousemove', this.handleResizeMouseMove, { passive: false });
    document.addEventListener('mouseup', this.handleResizeMouseUp, { passive: true });
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };

  private handleResizeMouseMove = (event: MouseEvent) => {
    if (!this.isDragging) return;

    const deltaX = this.startX - event.clientX;
    const newWidth = Math.max(
      this.configuredMinWidth,
      Math.min(AhaSidepanel.MAX_WIDTH, this.startWidth + deltaX),
    );

    this.setWidth(newWidth);
  };

  private handleResizeMouseUp = () => {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.isResizing = false;

    this.host.removeAttribute('data-resizing');

    document.removeEventListener('mousemove', this.handleResizeMouseMove);
    document.removeEventListener('mouseup', this.handleResizeMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  private handleResizeDoubleClick = () => {
    this.setWidth(this.configuredMinWidth);
  };

  render() {
    return (
      <Host>
        <div
          id="container"
          class={`${this.open ? 'open' : ''} ${this.isResizing ? 'resizing' : ''}`}
        >
          <div>
            <div class="pull-tabs">
              <svg class="rounded-edge rounded-edge--top" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 8 Q8 8 8 0 L8 8Z" fill="currentColor" />
              </svg>
              <aha-sidepanel-item
                onClick={this.handleClick}
                class={`main-pull-tab ${this.open ? 'rotated' : ''}`}
              >
                <aha-icon icon={`fa-regular fa-chevrons-left`} />
              </aha-sidepanel-item>
              <div onClick={() => (this.open = true)}>
                <slot name="pull-tabs" />
              </div>
              <svg class="rounded-edge rounded-edge--bottom" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0 Q8,0 8,8 L8 0Z" fill="currentColor" />
              </svg>
            </div>
          </div>

          <div class="shadow-layer"></div>
          <div class="content">
            {this.open && (
              <div
                class="resize-handle"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize sidepanel"
                tabIndex={0}
                onMouseDown={this.handleResizeMouseDown}
                onDblClick={this.handleResizeDoubleClick}
                title={
                  this.currentWidth !== this.configuredMinWidth
                    ? 'Double click to reset sidepanel back to default width'
                    : ''
                }
              ></div>
            )}
            <div class="content-wrapper">
              <slot></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
